import { MessageType, encodeText, smoothRoundTrip, type PerfReport } from "odyssey-shared";
import { keepaliveToken } from "../chat/ping.ts";

/**
 * One connection's side of the performance monitor (ADR-087 decisions 2 and 6): the bytes it has received, the round
 * trip of its last keepalive `ping` and those round trips smoothed, which lands a shot on the page (ADR-095 decision
 * 17), whether the gateway offered the server half, and the last report it sent. The
 * connection counts into it as frames arrive; the monitor reads it at 2 Hz and asks through it. Nothing here runs per
 * drawn frame.
 */

/**
 * A text frame's sender: false when the connection is not joined and open, so nothing was sent.
 */
export type TextSender = (text: string) => boolean;

/**
 * The perf state of one connection.
 */
export class PerfLink {
  private received = 0;
  private rtt: number | null = null;
  private smoothed: number | null = null;
  private interval: number | null = null;
  private last: PerfReport | null = null;
  private lastAt = 0;

  /**
   * @param send Sends a text frame on the connection.
   * @param roundTripMs The smoothed round trip the page's connection before this one had measured, which this one
   *   reads until its own first sample is back (ADR-095 note 52); null, or no number of 0 or more, for a page that
   *   has measured none.
   * @param sampled Hears each keepalive's round trip as it is taken (P1.9-T46).
   */
  public constructor(
    private readonly send: TextSender,
    roundTripMs: number | null = null,
    private readonly sampled: ((rttMs: number) => void) | null = null,
  ) {
    this.smoothed = roundTripMs !== null && roundTripMs >= 0 && Number.isFinite(roundTripMs) ? roundTripMs : null;
  }

  /**
   * Bytes received on the connection so far, text and binary.
   *
   * @returns The total.
   */
  public get bytes(): number {
    return this.received;
  }

  /**
   * The last keepalive's round trip in milliseconds, null before one came back.
   *
   * @returns The round trip.
   */
  public get rttMs(): number | null {
    return this.rtt;
  }

  /**
   * The keepalives' round trips smoothed (`smoothRoundTrip`), so one slow pong moves it by a share only; null before
   * one came back on a page whose connection before this one had measured none.
   *
   * @returns The smoothed round trip in milliseconds.
   */
  public get smoothedRttMs(): number | null {
    return this.smoothed;
  }

  /**
   * The interval the gateway's offer named, null before an offer, when the page must not ask.
   *
   * @returns The interval in milliseconds, or null.
   */
  public get offeredMs(): number | null {
    return this.interval;
  }

  /**
   * The last report and when it came, on the page clock.
   *
   * @returns The report and its time, or null before any.
   */
  public get report(): { readonly report: PerfReport; readonly at: number } | null {
    return this.last === null ? null : { report: this.last, at: this.lastAt };
  }

  /**
   * Counts a received frame.
   *
   * @param bytes Its length: a binary frame's bytes, a text frame's characters.
   */
  public count(bytes: number): void {
    this.received += bytes;
  }

  /**
   * Takes a `pong`: one whose token is even answers a keepalive, sent with the page clock's whole milliseconds with the
   * lowest bit cleared, so its round trip is the clock now less the token. An odd token is `/ping`'s and is ignored.
   * The connection's first sample is taken whole, in place of a figure carried from the connection before, which
   * serves only until then: the page may have come back on another network. Later samples move the value by their
   * share.
   *
   * @param t The echoed token.
   * @param now The page clock.
   */
  public pong(t: number, now: number): void {
    if (t % 2 === 0) {
      const rtt = now - t;
      if (rtt >= 0 && rtt < 60_000) {
        this.smoothed = smoothRoundTrip(this.rtt === null ? null : this.smoothed, rtt);
        this.rtt = rtt;
        this.sampled?.(rtt);
      }
    }
  }

  /**
   * Takes the gateway's offer.
   *
   * @param intervalMs The interval it names.
   */
  public offered(intervalMs: number): void {
    this.interval = intervalMs;
  }

  /**
   * Takes a report.
   *
   * @param report The report.
   * @param now The page clock.
   */
  public reported(report: PerfReport, now: number): void {
    this.last = report;
    this.lastAt = now;
  }

  /**
   * Asks for the server half, only after an offer.
   *
   * @returns True when a request was sent.
   */
  public request(): boolean {
    return this.interval !== null && this.send(encodeText({ type: MessageType.PerfRequest }));
  }

  /**
   * Sends a keepalive `ping` now, whose `pong` times the round trip.
   *
   * @param now The page clock.
   * @returns True when it was sent.
   */
  public ping(now: number): boolean {
    return this.send(encodeText({ type: MessageType.Ping, t: keepaliveToken(now) }));
  }
}
